import { useEffect } from "react";
import useCommon from "hooks/useCommon";
import { getArrivals } from "store/slice/arrivalsSlice";
import NoResults from "components/NoResult";
import { useMemo, useState } from "react";
import Table from "examples/Tables/Table";
import { GuestProfileName } from "components/GuestProfileDrawer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import { FaEye, FaList, FaThLarge } from "react-icons/fa";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ArrivalDetailsDialog from "./ArrivalsDetailsDialog";
import dayjs from "utils/dayjs";
import VuiTooltip from "components/VuiTooltip";
import CardPagination from "components/CardPagination";
import ArrivalCard from "./ArrivalCrad";
import VuiSwitch from "components/VuiSwitch";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import CardSkeleton from "components/Skeletons/CardSkeleton";

const { dark, gradients, primary } = colors;

const shimmer = {
  background:
    "linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.4s infinite",
};

const shimmerKeyframes = {
  "@keyframes shimmer": {
    "0%": { backgroundPosition: "-200% 0" },
    "100%": { backgroundPosition: "200% 0" },
  },
};

const rangeOptions = [
  { value: "today", label: "Today", tooltip: "View Today's Arrivals" },
  { value: "upcoming", label: "Upcoming", tooltip: "View Upcoming Arrivals" },
  { value: "all", label: "All", tooltip: "View All Arrivals" },
];

const Line = ({ w = "100%", h = 12, mb = 0.6 }) => (
  <VuiBox width={w} height={h} mb={mb} sx={{ ...shimmer, borderRadius: "6px" }} />
);

const GuestCell = ({ name }) => (
  <VuiBox>
    <VuiTypography variant="body2" fontWeight="regular">
      {name}
    </VuiTypography>
  </VuiBox>
);

const RoomCell = ({ room }) => (
  <VuiBox
    px={1.5}
    py={0.5}
    border="1px solid rgba(255, 255, 255, 0.3)"
    borderRadius="9px"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
  >
    <VuiTypography variant="caption">{room}</VuiTypography>
  </VuiBox>
);

const DateTimeCell = ({ date, time }) => (
  <VuiBox display="flex" flexDirection="column" lineHeight={1.5}>
    <VuiTypography mb={0.5} variant="caption" fontWeight="medium" sx={{ fontSize: "14px" }}>
      {date}
    </VuiTypography>
    <VuiTypography variant="caption">{time}</VuiTypography>
  </VuiBox>
);

const RequestsCell = ({ requests }) => (
  <VuiTypography variant="caption">{requests || "—"}</VuiTypography>
);

const OccasionCell = ({ occasion }) =>
  occasion ? (
    <VuiBadge variant="gradient" color="success" size="md" badgeContent={occasion} />
  ) : (
    "—"
  );

const ActionCell = ({ reservationId, onView }) => (
  <VuiBox display="flex" justifyContent="center" gap={1}>
    <VuiButton size="small" variant="outlined" onClick={() => onView(reservationId)}>
      <FaEye size={12} />
    </VuiButton>
  </VuiBox>
);

const DeparturesTable = ({ range, setRange, search, dateRange }) => {
  const { STORE, DISPATCH } = useCommon();
  const [view, setView] = useState("card"); // "table" | "card"
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const hotelIds = STORE((state) => state.ui.hotelIds);
  const { list: departures, fetchLoading, pagination } = STORE((state) => state.arrivals);

  const [start, end] = dateRange || [];

  const handleRangeChange = (e) => {
    const value = e.target.value;
    setRange(value);
    setPage(0);
  };

  const handleViewDetails = (reservationId) => {
    setSelectedReservationId(reservationId);
    setDetailsOpen(true);
  };

  useEffect(() => {
    if (!hotelIds?.length) return;

    // prevent API spam
    if (debouncedSearch && debouncedSearch.length < 3) return;

    const payload = {
      type: "departures",
      range,
      hotelIds,
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy: "datetime",
      sortDir: "asc",
    };

    if (debouncedSearch) payload.search = debouncedSearch;

    if (range === "today") {
      payload.fromUtc = dayjs.tz().startOf("day").utc().toISOString();

      payload.toUtc = dayjs.tz().endOf("day").utc().toISOString();
    }

    if (range === "all") {
      if (start) payload.fromUtc = dayjs.tz(start).startOf("day").utc().toISOString();
      if (end) payload.toUtc = dayjs.tz(end).endOf("day").utc().toISOString();
    }

    DISPATCH(getArrivals(payload));
  }, [hotelIds, page, rowsPerPage, range, debouncedSearch, dateRange, DISPATCH]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const tableData = useMemo(
    () => ({
      columns: [
        { name: "guest", align: "left" },
        { name: "room", align: "left" },
        { name: "departure", align: "center" },
        { name: "occasion", align: "center" },
        { name: "actions", align: "center" },
      ],
      rows: departures?.map((r) => {
        const date = dayjs.tz(r.datetime).format("MMM D");
        const time = dayjs.tz(r.datetime).format("h:mm A");

        return {
          guest: (
            <GuestProfileName
              guestId={r.guestId}
              hotelId={r.hotelId}
              name={r.guestName}
              sx={{ fontSize: 14 }}
            />
          ),
          room: <RoomCell room={r.roomNumber || "TBD"} />,
          departure: <DateTimeCell date={date} time={time} />,
          occasion: <OccasionCell occasion={r.occasion} />,
          actions: <ActionCell reservationId={r._id} onView={handleViewDetails} />,
        };
      }),
    }),
    [departures]
  );

  const isInitialEmpty = !fetchLoading && (!departures || departures.length === 0);

  return (
    <>
      <VuiBox px={{ xs: 1, md: 2 }} mb={{ xs: 2, md: 1 }}>
        <VuiBox
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={{ xs: 1, md: 0 }}
        >
          {/* ===== Range Selector ===== */}
          <RadioGroup
            row
            value={range}
            onChange={handleRangeChange}
            sx={{
              flexWrap: "nowrap",
              "& .MuiFormControlLabel-root": {
                mr: { xs: 1, sm: 2, md: 3 },
                ml: 0,
              },
            }}
          >
            {rangeOptions.map((item) => (
              <VuiTooltip key={item.value} title={item.tooltip}>
                <FormControlLabel
                  value={item.value}
                  control={<Radio size="small" />}
                  label={
                    <VuiTypography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </VuiTypography>
                  }
                />
              </VuiTooltip>
            ))}
          </RadioGroup>

          {/* ===== View Toggle ===== */}
          <VuiBox display="flex" justifyContent="flex-end">
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, val) => val && setView(val)}
              size="small"
              sx={{
                background: "rgba(255,255,255,0.04)",
                borderRadius: "10px",

                "& .MuiToggleButton-root": {
                  border: "none",
                  color: primary.main,
                  px: 1.5,
                },
              }}
            >
              <ToggleButton value="table">
                <FaList size={12} />
              </ToggleButton>
              <ToggleButton value="card">
                <FaThLarge size={12} />
              </ToggleButton>
            </ToggleButtonGroup>
          </VuiBox>
        </VuiBox>
      </VuiBox>

      {isInitialEmpty ? (
        <NoResults title="No departures available" />
      ) : view === "table" ? (
        <Table
          columns={tableData.columns}
          rows={tableData.rows}
          loading={fetchLoading}
          pagination
          paginationMode="server"
          page={page}
          rowsPerPage={rowsPerPage}
          total={pagination?.total || 0}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newLimit) => {
            setRowsPerPage(newLimit);
            setPage(0);
          }}
        />
      ) : (
        <>
          <VuiBox
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))"
            gap={2}
            px={{ xs: 0, md: 2 }}
            sx={{
              opacity: fetchLoading ? 0.6 : 1,
              transition: "0.2s ease",
            }}
          >
            {fetchLoading
              ? Array.from({ length: rowsPerPage }).map((_, i) => (
                  <CardSkeleton key={i} lines={4} hasHeader hasChips hasFooter />
                ))
              : departures.map((r) => (
                  <ArrivalCard
                    key={r._id}
                    data={r}
                    isDeparture
                    onView={() => {
                      handleViewDetails(r._id);
                    }}
                    onPreferences={() => handleOpenPreferencesPreview(r._id, r.hotelId)}
                  />
                ))}
          </VuiBox>

          <CardPagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={pagination?.total || 0}
            loading={fetchLoading}
            onPageChange={(newPage) => setPage(newPage)}
            onRowsPerPageChange={(newLimit) => {
              setRowsPerPage(newLimit);
              setPage(0);
            }}
          />
        </>
      )}

      <ArrivalDetailsDialog
        open={detailsOpen}
        reservationId={selectedReservationId}
        onClose={() => setDetailsOpen(false)}
        mode="departure"
      />
    </>
  );
};

export default DeparturesTable;
